import { Children, isValidElement, useId } from 'react';

import { css, styled } from '@mui/material';

import { StackDirection } from '../../constants/stack-direction';
import { InlineMessage } from '../InlineMessage';
import { Label } from '../Label';
import { LabelContainer } from '../LabelContainer';
import { getInlineMessageSize } from './constants';
import { formFieldClasses } from './theme';
import { isCheckboxGroup, isRadioGroup, isSelect, isSwitch, isTextInputLike } from './utils';

import type { ReactElement } from 'react';
import type { InlineMessageProps } from '../InlineMessage';
import type { LabelProps } from '../Label';
import type { FormFieldOwnerState, FormFieldProps } from './types';

const FormField = (props: FormFieldProps) => {
  const {
    children: childrenProp,
    direction = StackDirection.Vertical,
    disabled,
    error,
    inlineMessage: inlineMessageProp,
    label: labelProp,
    labelActions,
    labelPosition = 'top',
    size = 'md',
    ...others
  } = props;
  const generatedLabelId = useId();
  const explicitLabelId =
    isValidElement(labelProp) && labelProp.type === Label
      ? (labelProp as ReactElement<LabelProps, typeof Label>).props.id
      : undefined;
  const labelId = labelProp ? (explicitLabelId ?? `${generatedLabelId}-label`) : undefined;

  const children = Children.toArray(childrenProp).map(child => {
    if (isValidElement(child)) {
      if (isTextInputLike(child)) {
        return {
          ...child,
          props: {
            ...child.props,
            disabled: child.props.disabled ?? disabled,
            error: child.props.error ?? error,
            size: child.props.size ?? size,
          },
        };
      }

      if (isSelect(child)) {
        const Component = child.type;
        const ariaLabelledBy =
          child.props['aria-labelledby'] ?? (child.props['aria-label'] ? undefined : labelId);
        return (
          <Component
            key={child.key}
            {...child.props}
            aria-labelledby={ariaLabelledBy}
            disabled={child.props.disabled ?? disabled}
            error={child.props.error ?? error}
            size={child.props.size ?? size}
          />
        );
      }

      if (isRadioGroup(child)) {
        const Component = child.type;
        return (
          <Component
            key={child.key}
            {...child.props}
            direction={child.props.direction ?? direction}
            disabled={child.props.disabled ?? disabled}
            error={child.props.error ?? error}
          />
        );
      }

      if (isCheckboxGroup(child)) {
        const Component = child.type;
        return (
          <Component
            key={child.key}
            {...child.props}
            direction={child.props.direction ?? direction}
            disabled={child.props.disabled ?? disabled}
            error={child.props.error ?? error}
          />
        );
      }

      if (isSwitch(child)) {
        const Component = child.type;
        return (
          <Component
            key={child.key}
            {...child.props}
            disabled={child.props.disabled ?? disabled}
            size={child.props.size ?? size}
          />
        );
      }
    }
    return child;
  });

  const grouped = children.length > 1;

  const ownerState: FormFieldOwnerState = {
    ...props,
    direction,
    labelPosition,
    size,
    grouped,
  };

  let label = labelProp;
  if (labelProp) {
    let passingProps: LabelProps = {
      label: '',
      disabled,
      id: labelId,
      size: 'md',
      type: 'secondary',
    };
    if (typeof labelProp === 'string') {
      passingProps = { ...passingProps, label: labelProp };
    }
    if (isValidElement(labelProp) && labelProp.type === Label) {
      const element = labelProp as ReactElement<LabelProps, typeof Label>;
      passingProps = { ...passingProps, ...element.props };
    }
    label = <FormFieldLabel {...passingProps} ownerState={ownerState} />;
  }

  let inlineMessage = inlineMessageProp;
  if (inlineMessageProp) {
    let passingProps: InlineMessageProps = {
      disabled,
      icon: false,
      size: size && getInlineMessageSize(size),
      variant: error ? 'error' : 'help',
    };
    if (typeof inlineMessageProp === 'string') {
      passingProps = { ...passingProps, message: inlineMessageProp };
    }
    if (isValidElement(inlineMessageProp) && inlineMessageProp.type === InlineMessage) {
      const element = inlineMessageProp as ReactElement<InlineMessageProps, typeof InlineMessage>;
      passingProps = { ...passingProps, ...element.props };
    }
    inlineMessage = <FormFieldInlineMessage {...passingProps} ownerState={ownerState} />;
  }

  return (
    <FormFieldRoot ownerState={ownerState} {...others}>
      {label && (
        <FormFieldLabelContainer
          ownerState={ownerState}
          label={label as ReactElement<LabelProps, typeof Label>}
          actions={labelActions}
        />
      )}
      <FormFieldContentContainer ownerState={ownerState}>
        <FormFieldContent ownerState={ownerState}>{children}</FormFieldContent>
        {inlineMessage}
      </FormFieldContentContainer>
    </FormFieldRoot>
  );
};

const FormFieldRoot = styled('div', {
  target: formFieldClasses.root,
})<{ ownerState: FormFieldOwnerState }>(({ ownerState }) => {
  const { labelPosition } = ownerState;

  return css`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: ${labelPosition === 'top' ? 'column' : 'row'};
  `;
});

const FormFieldLabelContainer = styled(LabelContainer, {
  target: formFieldClasses.labelContainer,
})<{ ownerState: FormFieldOwnerState }>(({ ownerState }) => {
  const { labelPosition, size } = ownerState;

  // labelPosition 스타일
  const positionStyle =
    labelPosition === 'top'
      ? css`
          margin-bottom: 2px;
        `
      : css`
          min-width: 140px;
          margin-right: 16px;
        `;

  // size 스타일
  const sizeStyle =
    size === 'sm'
      ? css`
          padding: 4px 0;
        `
      : css`
          padding: 6px 0;
        `;

  return css`
    ${positionStyle}
    ${sizeStyle}
  `;
});

const FormFieldLabel = styled(Label, {
  target: formFieldClasses.label,
})<{ ownerState: FormFieldOwnerState }>``;

const FormFieldContentContainer = styled('div', {
  target: formFieldClasses.contentContainer,
})<{ ownerState: FormFieldOwnerState }>``;

const FormFieldContent = styled('div', {
  target: formFieldClasses.content,
})<{ ownerState: FormFieldOwnerState }>(({ ownerState }) => {
  const { grouped, direction = StackDirection.Vertical } = ownerState;

  // grouped일 때만 display: flex와 gap 적용
  if (!grouped) {
    return css``;
  }

  return css`
    display: flex;
    gap: 8px;
    flex-direction: ${StackDirection.toFlexDirection(direction)};
  `;
});

const FormFieldInlineMessage = styled(InlineMessage, {
  target: formFieldClasses.inlineMessage,
})<{ ownerState: FormFieldOwnerState }>`
  margin-top: 2px;
  word-break: break-all;
`;

export default FormField;
